/**
 * Build Slot Search dropdown sections for insightInstance===1 casinos.
 *
 * Up to four collapsible sections (omit empty), top → bottom:
 * 1. Floor — floorLocation (min 2, space-normalized prefix) / 2-char bank hits,
 *    grouped by "Floor <section><bank>" labels ("Floor I4"). Collapsed unless alone.
 * 2. Asset — mnum or machines.slotNumber prefix hits (startsWith, min 2), one
 *    row per mnum labeled "Asset <displayId>" (slotNumber, else mnum) with game
 *    name/title underneath. Selection still uses mnum. Collapsed unless alone.
 * 3. Manufacturer — effective manufacturer prefix hits (startsWith, min 3;
 *    slotcheckManufacturer || manufacturer), grouped by that label. Collapsed
 *    default unless alone.
 * 4. Game Names — name/title/gameType (min 3) / progressive hits. Open by default.
 *
 * When Floor, Asset, or Manufacturer has exactly one group, the section and row
 * are folded (section title becomes the group label; games list directly under it).
 * When the result has only one section, that section opens by default.
 *
 * A section appears only when its field meets its minimum and has matches.
 *
 * Name / title / manufacturer grouping is case- and whitespace-insensitive
 * (see {@link groupLabelKey}): "DRAGON LINK", "Dragon Link", "Dragon\u00a0Link"
 * and "Dragon  Link" are ONE group whose count is the sum of all spellings and
 * whose slots include every machine. The label shown is the most common
 * original spelling (ties → first seen), with whitespace collapsed; Search.tsx
 * still applies titleCase to game-name headers.
 *
 * Group rows keep the shape Search.tsx expects:
 *   { game_name, total, sub_game_names, other_sub_games, preserveHeaderCasing? }
 */

import {
  InsightSearchableMachine,
  filterInsightMachinesByQuery,
  floorLocationShortLabel,
  matchesGameFieldsQuery,
  matchesLocationQuery,
  matchesManufacturerQuery,
  matchesAssetQuery,
  normalizeSearchText,
  pickGameName,
  pickGameTitle,
  pickManufacturer,
} from './insightSearchMatch';
import { displayIdForInsightMnum, getActiveMachinesByMnum } from './machineCatalog';
import { getMachineDisplayId } from './machineDisplayId';

export type InsightSearchSlot = {
  game_name: string;
  /** Placement / floor code (alias of floorLocation for insight rows). */
  slot_number: number | string;
  /** Machine id (mnum). SelectedSlots / map pins must use this, not slot_number. */
  mnum?: string | number | null;
  denom: string;
  type: string;
};

export type InsightSearchSubGroup = {
  game_name: string;
  type: string;
  denom: string;
  slots: InsightSearchSlot[];
};

export type InsightSearchGroup = {
  game_name: string;
  total: number;
  sub_game_names: InsightSearchSubGroup[];
  other_sub_games: InsightSearchSubGroup[];
  /** Keep manufacturer / Bank header casing in the UI (no titleCase). */
  preserveHeaderCasing?: boolean;
  /**
   * Machine id when this row is a single Asset (or other single-mnum) result.
   * Search taps must open Details with this mnum — never floorLocation/slot_number.
   */
  mnum?: string | number | null;
};

export type InsightSearchSectionId = 'floor' | 'asset' | 'manufacturer' | 'gameName';

export type InsightSearchSection = {
  id: InsightSearchSectionId;
  title: string;
  /** Machine count in this section (for the section header). */
  total: number;
  defaultOpen: boolean;
  groups: InsightSearchGroup[];
  /**
   * When true, the section header is the sole group (e.g. "Floor I4" / "AGS")
   * and the UI lists games directly under it — no nested group row.
   */
  flat?: boolean;
};

function slotOf(m: InsightSearchableMachine, type: string): InsightSearchSlot {
  const sn = m.slot_number ?? m.floorLocation ?? '';
  const mnum =
    m.mnum !== null && m.mnum !== undefined && String(m.mnum).trim() !== ''
      ? m.mnum
      : null;
  return {
    game_name: pickGameName(m) || pickGameTitle(m) || String(sn),
    slot_number: sn as string | number,
    mnum,
    denom: String((m as { denom?: string | null }).denom ?? ''),
    type,
  };
}

/**
 * Grouping key for name / title / manufacturer labels: lowercase, every
 * Unicode whitespace run (incl. NBSP) collapsed to one space, trimmed.
 * Same normalization the matcher uses, so what matches together groups together.
 */
export function groupLabelKey(value: string | null | undefined): string {
  return normalizeSearchText(value);
}

/** Display form of an original spelling: whitespace runs (incl. NBSP) → one space, trimmed. */
function cleanLabel(value: string): string {
  return String(value ?? '')
    .replace(/[\s\u00a0]+/g, ' ')
    .trim();
}

function namesEqual(a: string, b: string): boolean {
  return groupLabelKey(a) === groupLabelKey(b);
}

export type LabelBucket<T> = {
  /** Normalized key ({@link groupLabelKey}). */
  key: string;
  /** Most common original spelling (ties → first seen), whitespace-collapsed. */
  label: string;
  items: T[];
};

/**
 * Bucket items by {@link groupLabelKey} of `labelOf(item)`, keeping first-seen
 * order. Items whose label is blank land in the '' bucket (callers decide
 * whether to show it).
 */
export function groupByLabel<T>(items: T[], labelOf: (item: T) => string): LabelBucket<T>[] {
  const buckets = new Map<
    string,
    { items: T[]; spellings: Map<string, { count: number; order: number }> }
  >();
  for (const item of items) {
    const raw = cleanLabel(labelOf(item));
    const key = groupLabelKey(raw);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { items: [], spellings: new Map() };
      buckets.set(key, bucket);
    }
    bucket.items.push(item);
    const seen = bucket.spellings.get(raw);
    if (seen) seen.count += 1;
    else bucket.spellings.set(raw, { count: 1, order: bucket.spellings.size });
  }
  return Array.from(buckets.entries()).map(([key, { items: bucketItems, spellings }]) => {
    let label = '';
    let best: { count: number; order: number } | null = null;
    for (const [spelling, stat] of Array.from(spellings.entries())) {
      const better =
        !best || stat.count > best.count || (stat.count === best.count && stat.order < best.order);
      if (better) {
        best = stat;
        label = spelling;
      }
    }
    return { key, label, items: bucketItems };
  });
}

/**
 * Title children under a game-name header.
 * - Null / blank titles are counted on the parent group but do NOT create a
 *   blank (or name-echo) child row.
 * - Omits a child when it would only repeat the parent name.
 */
function titleChildren(
  machines: InsightSearchableMachine[],
  parentName: string
): InsightSearchSubGroup[] {
  // Null/empty title → counted in group.total via nameGroups, no child row.
  const subs = groupByLabel(machines, (m) => pickGameTitle(m))
    .filter((b) => b.key !== '')
    .map(({ label: title, items: ms }) => ({
      game_name: title,
      type: 'title',
      denom: String((ms[0] as { denom?: string | null })?.denom ?? ''),
      slots: ms.map((m) => slotOf(m, 'title')),
    }))
    .sort((a, b) => a.game_name.localeCompare(b.game_name));

  // Don't repeat the parent name as a child row.
  const distinct = subs.filter((s) => !namesEqual(s.game_name, parentName));
  if (distinct.length === 0) return [];
  return distinct;
}

/** Indented game/title rows under a Floor or manufacturer header. */
function indentedGameRows(machines: InsightSearchableMachine[]): InsightSearchSubGroup[] {
  const nameBuckets = groupByLabel(
    machines,
    (m) => pickGameName(m) || pickGameTitle(m) || 'Unknown'
  ).sort((a, b) => a.label.localeCompare(b.label));
  const subs: InsightSearchSubGroup[] = [];
  for (const { label: name, items: nameMs } of nameBuckets) {
    const titleEntries: Array<[string, InsightSearchableMachine[]]> = groupByLabel(
      nameMs,
      (m) => pickGameTitle(m) || name
    )
      .map((b): [string, InsightSearchableMachine[]] => [b.label, b.items])
      .sort((a, b) => a[0].localeCompare(b[0]));
    // Sole title equal to name → one game row labeled with the name (not name+title dup).
    if (titleEntries.length === 1 && namesEqual(titleEntries[0][0], name)) {
      const titleMs = titleEntries[0][1];
      subs.push({
        game_name: name,
        type: 'game',
        denom: String((titleMs[0] as { denom?: string | null })?.denom ?? ''),
        slots: titleMs.map((m) => slotOf(m, 'game')),
      });
      continue;
    }
    for (const [title, titleMs] of titleEntries) {
      // Skip a title row that only repeats the game name when other titles exist.
      if (namesEqual(title, name) && titleEntries.length > 1) continue;
      const rowLabel = title && !namesEqual(title, name) ? title : name;
      subs.push({
        game_name: rowLabel,
        type: 'game',
        denom: String((titleMs[0] as { denom?: string | null })?.denom ?? ''),
        slots: titleMs.map((m) => slotOf(m, 'game')),
      });
    }
  }
  return subs;
}

function nameGroups(machines: InsightSearchableMachine[]): InsightSearchGroup[] {
  // One group per normalized name ("DRAGON LINK" + "Dragon Link" → one row);
  // total = machines across every spelling.
  return groupByLabel(machines, (m) => pickGameName(m) || pickGameTitle(m) || 'Unknown')
    .map(({ label: name, items: ms }) => {
      const subs = titleChildren(ms, name);
      // other_sub_games keeps all slots for selection even when UI hides redundant children.
      const allSubs = titleChildrenForSelect(ms);
      return {
        game_name: name,
        total: ms.length,
        sub_game_names: subs,
        other_sub_games: allSubs.length ? allSubs : [{
          game_name: name,
          type: 'title',
          denom: String((ms[0] as { denom?: string | null })?.denom ?? ''),
          slots: ms.map((m) => slotOf(m, 'title')),
        }],
      };
    })
    .sort((a, b) => b.total - a.total || a.game_name.localeCompare(b.game_name));
}

/**
 * Full title children for selection. Null-title machines are kept under the
 * parent game name so tapping the group still selects them.
 */
function titleChildrenForSelect(machines: InsightSearchableMachine[]): InsightSearchSubGroup[] {
  return groupByLabel(machines, (m) => pickGameTitle(m) || pickGameName(m) || 'Unknown')
    .map(({ label: title, items: ms }) => ({
      game_name: title,
      type: 'title',
      denom: String((ms[0] as { denom?: string | null })?.denom ?? ''),
      slots: ms.map((m) => slotOf(m, 'title')),
    }))
    .sort((a, b) => a.game_name.localeCompare(b.game_name));
}

function manufacturerGroups(machines: InsightSearchableMachine[]): InsightSearchGroup[] {
  // Blank manufacturer → no row (same as before); spellings merge by groupLabelKey.
  return groupByLabel(machines, (m) => pickManufacturer(m))
    .filter((b) => b.key !== '')
    .map(({ label, items: ms }) => {
      const subs = indentedGameRows(ms);
      return {
        game_name: label,
        total: ms.length,
        sub_game_names: subs,
        other_sub_games: subs,
        preserveHeaderCasing: true,
      };
    })
    .sort((a, b) => b.total - a.total || a.game_name.localeCompare(b.game_name));
}

function floorLocationGroups(machines: InsightSearchableMachine[]): InsightSearchGroup[] {
  const byLoc = new Map<string, InsightSearchableMachine[]>();
  for (const m of machines) {
    const key = floorLocationShortLabel(m);
    const list = byLoc.get(key) ?? [];
    list.push(m);
    byLoc.set(key, list);
  }

  return Array.from(byLoc.entries())
    .map(([label, ms]) => {
      const subs = indentedGameRows(ms);
      return {
        game_name: label,
        total: ms.length,
        sub_game_names: subs,
        other_sub_games: subs,
        preserveHeaderCasing: true,
      };
    })
    .sort((a, b) => b.total - a.total || a.game_name.localeCompare(b.game_name));
}

function sectionTotal(groups: InsightSearchGroup[]): number {
  return groups.reduce((n, g) => n + (g.total || 0), 0);
}

/** Label for an Asset row, e.g. "Asset 50804" (display id) or "Asset 2807" (mnum fallback). */
export function assetRowLabel(displayId: string | number): string {
  return `Asset ${String(displayId).trim()}`;
}

/**
 * Game name / title children under an Asset row. Null titles omitted;
 * title equal to name is not repeated.
 */
function assetNameTitleChildren(m: InsightSearchableMachine): InsightSearchSubGroup[] {
  const name = pickGameName(m);
  const title = pickGameTitle(m);
  const slots = [slotOf(m, 'asset')];
  const denom = String((m as { denom?: string | null }).denom ?? '');
  const rows: InsightSearchSubGroup[] = [];
  if (name) {
    rows.push({ game_name: name, type: 'asset', denom, slots });
  }
  if (title && (!name || !namesEqual(title, name))) {
    rows.push({ game_name: title, type: 'asset', denom, slots });
  }
  if (!rows.length) {
    rows.push({
      game_name: String(m.mnum ?? m.slot_number ?? m.floorLocation ?? 'Unknown'),
      type: 'asset',
      denom,
      slots,
    });
  }
  return rows;
}

/** One group per matching mnum: "Asset <mnum>" + name/title children. */
function assetGroups(machines: InsightSearchableMachine[]): InsightSearchGroup[] {
  const byMnum = new Map<string, InsightSearchableMachine[]>();
  for (const m of machines) {
    const key = String(m.mnum ?? '').trim();
    if (!key) continue;
    const list = byMnum.get(key) ?? [];
    list.push(m);
    byMnum.set(key, list);
  }

  return Array.from(byMnum.entries())
    .map(([mnum, ms]) => {
      // Prefer a single machine's name/title; if several share an mnum, indent all games.
      const subs =
        ms.length === 1
          ? assetNameTitleChildren(ms[0])
          : indentedGameRows(ms);
      // DISPLAY: prefer active GetSearchData catalog (same as Compare/Explore);
      // else the machine row itself (tests / pre-hydrate). Selection stays mnum.
      const mnumKey = String(mnum).trim();
      const fromCatalog = getActiveMachinesByMnum()?.has(mnumKey)
        ? displayIdForInsightMnum(mnumKey)
        : '';
      const displayId = fromCatalog || getMachineDisplayId(ms[0]) || mnum;
      return {
        game_name: assetRowLabel(displayId),
        total: ms.length,
        // Group-level mnum so tapping opens Details with machine id
        // (not floorLocation). Name+title children must not block this.
        mnum,
        sub_game_names: subs,
        other_sub_games: subs,
        preserveHeaderCasing: true,
      };
    })
    .sort((a, b) => a.game_name.localeCompare(b.game_name, undefined, { numeric: true }));
}

/**
 * Flatten search slot rows from a group / sub-group (slots[] or other_sub_games).
 * Used by Search for SelectedSlots and single-machine Details opens.
 */
export function flattenInsightSearchSlotRows(item: {
  slots?: Array<{ mnum?: unknown; slot_number?: unknown }>;
  other_sub_games?: Array<{
    slots?: Array<{ mnum?: unknown; slot_number?: unknown }>;
    mnum?: unknown;
    slot_number?: unknown;
  }>;
  mnum?: unknown;
  slot_number?: unknown;
} | null | undefined): Array<{ mnum?: unknown; slot_number?: unknown }> {
  if (!item) return [];
  if (Array.isArray(item.slots) && item.slots.length > 0) return item.slots;
  const rows: Array<{ mnum?: unknown; slot_number?: unknown }> = [];
  if (Array.isArray(item.other_sub_games)) {
    for (const element of item.other_sub_games) {
      if (Array.isArray(element?.slots) && element.slots.length > 0) {
        rows.push(...element.slots);
      } else if (element) {
        rows.push(element);
      }
    }
  }
  if (rows.length > 0) return rows;
  if (
    (item.mnum !== null && item.mnum !== undefined && String(item.mnum).trim() !== '') ||
    (item.slot_number !== null &&
      item.slot_number !== undefined &&
      String(item.slot_number).trim() !== '')
  ) {
    return [{ mnum: item.mnum, slot_number: item.slot_number }];
  }
  return [];
}

/**
 * Fold a single Floor/Asset/Manufacturer group into the section header so we don't
 * show "Floor (8)" + "Floor I4 (8)" or "Manufacturer (24)" + "AGS (24)".
 */
function maybeFoldSingleGroup(
  id: InsightSearchSectionId,
  sectionTitle: string,
  groups: InsightSearchGroup[],
  defaultOpen: boolean
): InsightSearchSection | null {
  if (!groups.length) return null;
  if (groups.length === 1) {
    const g = groups[0];
    return {
      id,
      title: g.game_name,
      total: g.total,
      defaultOpen,
      flat: true,
      groups: [g],
    };
  }
  return {
    id,
    title: sectionTitle,
    total: sectionTotal(groups),
    defaultOpen,
    groups,
  };
}

/**
 * Build collapsible insight Slot Search sections for the typed query.
 * Empty / whitespace query returns [] (dropdown stays closed until the user types).
 */
export function buildInsightSearchSections(
  machines: InsightSearchableMachine[],
  query: string
): InsightSearchSection[] {
  const q = String(query ?? '').trim();
  if (!q) return [];

  const matched = filterInsightMachinesByQuery(machines, q);
  if (!matched.length) return [];

  const locationHits = matched.filter((m) => matchesLocationQuery(m, q));
  const assetHits = matched.filter((m) => matchesAssetQuery(m, q));
  const mfrHits = matched.filter((m) => matchesManufacturerQuery(m, q));
  const gameHits = matched.filter((m) => matchesGameFieldsQuery(m, q));

  const floorGroups = floorLocationGroups(locationHits);
  const assetSectionGroups = assetGroups(assetHits);
  const mfrGroups = manufacturerGroups(mfrHits);
  const gameGroups = nameGroups(gameHits);

  const sections: InsightSearchSection[] = [];
  // Section id stays 'floor' (data key); user-facing title is Bank.
  const floor = maybeFoldSingleGroup('floor', 'Bank', floorGroups, false);
  if (floor) sections.push(floor);
  const asset = maybeFoldSingleGroup('asset', 'Asset', assetSectionGroups, false);
  if (asset) sections.push(asset);
  const mfr = maybeFoldSingleGroup('manufacturer', 'Manufacturer', mfrGroups, false);
  if (mfr) sections.push(mfr);
  if (gameGroups.length) {
    sections.push({
      id: 'gameName',
      title: 'Game Names',
      total: sectionTotal(gameGroups),
      defaultOpen: true,
      groups: gameGroups,
    });
  }

  // Sole section → open by default (Floor / Asset / Manufacturer alone, or Game Names).
  if (sections.length === 1) {
    sections[0].defaultOpen = true;
  }
  return sections;
}

/**
 * Flat group list (legacy helper / tests that only need groups).
 * Prefer {@link buildInsightSearchSections} for the insight Slot Search UI.
 */
export function buildInsightSearchGroups(
  machines: InsightSearchableMachine[],
  query: string
): InsightSearchGroup[] {
  return buildInsightSearchSections(machines, query).flatMap((s) => s.groups);
}
